import { memo } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { down, up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import { FlexRow } from 'src/components//layout/flex-row';
import { TableColumns } from 'src/components/general/display/table';
import { Icon } from 'src/components/general/icon';
import { FlexColumn } from 'src/components/layout/flex-column';
import { parseQueryParams } from 'src/utils/parse-query-params';
import { stringifyQueryParams } from 'src/utils/stringify-query-params';

export type BasePageQueryParams<
  ViewCode,
  T = Map<string, number | string | number[] | string[]>,
> = {
  table?: string;
  institution_id?: string;
  after?: string;
  before?: string;
  institution_type?: string;
  q?: {
    advanced: T;
  };
  'view-code'?: 'custom' | ViewCode;
};

export type TableSortData = {
  key: string;
  direction: SortDirection;
};

export type SortOrder = 'ascend' | 'descend' | undefined;

export type SorterData<T> = {
  column: TableColumns<T>;
  columnKey: string;
  field: string;
  order: SortOrder;
};

export const getSortDirectionByOrder = (order: SortOrder) =>
  order ? (order === 'ascend' ? 'ASC' : 'DESC') : undefined;

export const getDefaultSortOrder = (isActive: boolean, sortOrder?: string) =>
  isActive && sortOrder ? (sortOrder === 'ASC' ? 'ascend' : 'descend') : null;

export const tableLayoutViewButtonStyles = css`
  border: 1px solid ${({ theme }) => theme.colors.koala};
  font-size: 13px;
  height: 32px;
  padding: 8px 10px;

  &,
  &:active,
  &:focus,
  &:hover {
    color: ${({ theme }) => theme.colors.gogo};
  }

  .arrow-down-icon {
    margin-left: 5px;

    ${up('md')} {
      margin-left: 10px;
    }
  }

  ${up('md')} {
    font-size: 14px;
    height: 38px;
    padding: 9px 15px;
    border: none;
  }
`;

export type TableLayoutFilterProps<T> = {
  visible: boolean;
  onClose: VoidFunction;
  filters?: Partial<T>;
  submitHandlerOverride?: (filters: Partial<T>) => void;
  onReset?: VoidFunction;
};

const HeaderContainer = styled(FlexColumn)`
  ${up('md')} {
    padding: 0;
  }
`;

const HeaderTopContainer = styled(FlexRow)`
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;

  ${up('md')} {
    margin-bottom: 30px;
    padding: 0;
  }
`;

const HeaderBottomContainer = styled(FlexRow)`
  background-color: ${({ theme }) => theme.colors.white};
  border-top: 1px solid ${({ theme }) => theme.colors.koala};
  padding: 10px 20px;

  ${up('md')} {
    background-color: transparent;
    border: none;
    margin-bottom: 30px;
    padding: 0;
  }
`;

const HeaderInnerContainer = styled(FlexRow)`
  & > *:not(:last-child) {
    margin-right: 10px;

    ${up('md')} {
      margin-right: 20px;
    }
  }
`;

export const MoreOptionsIcon = styled(Icon)`
  position: relative;
  left: -6px;

  & > svg {
    height: 16px;
    margin: 0 6px;
    width: 4px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

export const TableColumnTitle = styled.span`
  white-space: nowrap;
`;

export const TableLayoutFiltersContainer = styled(FlexRow)`
  & > * {
    height: 32px;
    width: 32px;
    padding: 7px 15px;

    ${up('md')} {
      height: 38px;
      width: unset;
    }

    ${down('md')} {
      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  & > *:first-child {
    border-radius: 4px 0 0 4px;

    ${up('md')} {
      margin-right: 20px;
      border-radius: 4px;
    }
  }

  & > *:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.colors.porcelain2};
  }

  & > *:last-child {
    border-radius: 0 4px 4px 0;
  }
`;

type TableLayoutHeaderProps = {
  leftContent?: JSX.Element;
  rightContent: JSX.Element;
  bottomContent?: JSX.Element;
};

export const TableLayoutHeader = memo<TableLayoutHeaderProps>(
  ({ leftContent, rightContent, bottomContent }) => {
    return (
      <HeaderContainer>
        <HeaderTopContainer>
          <HeaderInnerContainer>{leftContent}</HeaderInnerContainer>
          <HeaderInnerContainer>{rightContent}</HeaderInnerContainer>
        </HeaderTopContainer>
        {bottomContent && <HeaderBottomContainer>{bottomContent}</HeaderBottomContainer>}
      </HeaderContainer>
    );
  },
);

export const useTableSubmitHandler = <T,>(params: {
  onClose: VoidFunction;
  addViewCode?: boolean;
}) => {
  const location = useLocation();
  const history = useHistory();
  const { institutionId } = useParams<{ institutionId: string }>();
  const { onClose, addViewCode = true } = params;

  return (formValues: T) => {
    const queryParams = parseQueryParams(location.search);
    const sortParam = queryParams['table'] ? { table: queryParams['table'] } : {};

    const objectParams: T = {
      institution_id: institutionId,
      institution_type: 'company',
      ...(addViewCode ? { 'view-code': 'custom' } : {}),
      ...sortParam,
      q: {
        advanced: {
          ...formValues,
        },
      },
    } as T;

    const queryString = stringifyQueryParams(objectParams);
    const newUrl = `${location.pathname}?${queryString}`;
    onClose();
    history.push(newUrl);
  };
};

export const useSimpleTableSubmitHandler = <T,>(params: { onClose: VoidFunction }) => {
  const location = useLocation();
  const history = useHistory();

  const { onClose } = params;

  return (formValues: T) => {
    const queryParams = parseQueryParams(location.search);
    const sortParam = queryParams['table'] ? { table: queryParams['table'] } : {};

    const objectParams: T = {
      ...sortParam,
      ...formValues,
    } as T;

    const queryString = stringifyQueryParams(objectParams);
    const newUrl = `${location.pathname}?${queryString}`;
    onClose();
    history.push(newUrl);
  };
};

export const StyledTableFiltersForm = styled.form`
  & > * {
    margin-bottom: 18px;

    &:last-child {
      margin-bottom: 35px;
    }
  }
`;
